import { z } from 'zod';

export const createNewPasswordSchema = z.object({
  newPassword: z
    .string()
    .min(6, 'Password must be at least 6 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string().min(1, 'Confirm password is required'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
}); 




export const practiceInfoSchema = z.object({
  practiceName: z.string().min(1, 'Practice name is required'),
  officeAddress: z.string().min(1, 'Office address is required'),
  officeEmail: z.string().email('Invalid email address').min(1, 'Office email is required'),
  officePhone: z.string().min(1, 'Office phone is required').max(12, 'Phone number must be less than 12 characters')
    .regex(/^[0-9-]+$/, 'Phone number can only contain numbers and hyphens'),
  fax: z.string().optional().refine((value) => {
    if (!value || value === '') return true;
    // Remove all non-digit characters to check length
    const digitsOnly = value.replace(/\D/g, '');
    // Allow 10-15 digits (US to international range) with optional formatting
    return digitsOnly.length >= 10 && digitsOnly.length <= 15 && /^[\d\s\-\(\)\+\.]+$/.test(value);
  }, {
    message: 'Fax number must be 10-15 digits and can contain numbers, spaces, hyphens, parentheses, plus signs, and dots',
    path: ['fax'],
  }),
  practiceTypes: z.array(z.string()).min(1, 'Please select at least one practice type'),
  practiceUrl: z.string().min(1, 'Practice website is required').url('Invalid website URL')
});



export const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  middleName: z.string().optional(),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  workPhone: z.string()
    .min(1, 'Work phone is required')
    .refine((value) => {
      const digitsOnly = value.replace(/\D/g, '');
      return digitsOnly.length === 10;
    }, 'Work phone must contain exactly 10 digits'),
  role: z.array(z.string()).min(1, 'At least one role is required'),
});

export const reportIssueSchema = z.object({
  description: z.string().min(10, 'Description must be at least 10 characters long'),
  attachment: z
    .instanceof(FileList)
    .optional()
    .refine((files) => {
      if (!files || files.length === 0) return true;
      const file = files[0];
      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/plain'];
      
      if (file.size > maxSize) {
        return false;
      }
      
      if (!allowedTypes.includes(file.type)) {
        return false;
      }
      
      return true;
    }, 'File must be less than 5MB and be a valid file type (JPEG, PNG, GIF, PDF, or TXT)'),
});

export const appointmentRequestSchema = z.object({
  patientType: z.enum(['new', 'existing'], {
    required_error: 'Please select patient type',
  }),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  phone: z.string().min(1, 'Phone number is required').refine(value => {
    return /^[\d\s\-\(\)\+]+$/.test(value);
  }, {
    message: 'Invalid phone number format',
    path: ['phone'],
  }),
  // phone: z.string().optional().refine(value => {
  //   if (!value || value === '') return true;
  //   return /^[\d\s\-\(\)\+]+$/.test(value);
  // }, {
  //   message: 'Invalid phone number format',
  //   path: ['phone'],
  // }),
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  // email: z.string().optional().refine(value => {
  //   if (!value || value === '') return true;
  //   return z.string().email().safeParse(value).success;
  // }, {
  //   message: 'Invalid email address',
  //   path: ['email'],
  // }),
  preferredDays: z.array(z.string()).optional(),
  preferredTimes: z.array(z.string()).optional(),
  servicesInterested: z.string().optional(),
  message: z.string().optional(),
  source: z.array(z.string()).optional(),
  requestSource: z.string().optional(),
});

export type AppointmentRequestFormData = z.infer<typeof appointmentRequestSchema>;

export const providerReferralSchema = z.object({
  patientFirstName: z.string().min(1, 'Patient first name is required'),
  patientLastName: z.string().min(1, 'Patient last name is required'),
  patientPhone: z.string().optional().refine((value) => {
    if (!value || value === '') return true;
    // Remove all non-digit characters and check if it's exactly 10 digits
    const digitsOnly = value.replace(/\D/g, '');
    return digitsOnly.length === 10;
  }, {
    message: 'Please enter a valid US phone number (exactly 10 digits)',
    path: ['patientPhone'],
  }),
  patientEmail: z.string().optional().refine((value) => {
    if (!value || value === '') return true;
    return z.string().email().safeParse(value).success;
  }, {
    message: 'Please enter a valid email address',
    path: ['patientEmail'],
  }),
  referringDoctorName: z.string().min(1, 'Referring doctor name is required'),
  practiceName: z.string().min(1, 'Practice name is required'),
  doctorPhone: z.string().optional().refine((value) => {
    if (!value || value === '') return true;
    // Remove all non-digit characters and check if it's exactly 10 digits
    const digitsOnly = value.replace(/\D/g, '');
    return digitsOnly.length === 10;
  }, {
    message: 'Please enter a valid US phone number (exactly 10 digits)',
    path: ['doctorPhone'],
  }),
  doctorEmail: z.string().optional().refine((value) => {
    if (!value || value === '') return true;
    return z.string().email().safeParse(value).success;
  }, {
    message: 'Please enter a valid email address',
    path: ['doctorEmail'],
  }),
  selectedTeeth: z.array(z.number()).optional(),
  remarks: z.string().optional(),
});

export type ProviderReferralFormData = z.infer<typeof providerReferralSchema>;
